import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
	type ApiQueryAddonTier,
	addonQueriesForSliderIndex,
	addonTierForSliderIndex,
	effectiveApiQueries,
	formatQueryCount,
	selectAddonPriceId,
	sliderIndexForAddonQueries,
} from "@/utils/api-pricing";
import { formatMoney } from "@/utils/plan-checkout";

type ApiQueryAddonSliderProps = {
	planName: string;
	tiers: ApiQueryAddonTier[];
	isAnnual: boolean;
	currentAddonQueries?: number;
	currentAddonPriceId?: string;
	/** Controlled slider index: 0 = none, 1..n = tier. */
	value?: number;
	onValueChange?: (index: number) => void;
	onSelectAddon?: (priceId: string, addonQueries: number) => void;
	onRemoveAddon?: () => void;
	isUpdating?: boolean;
	/** Hide update/remove actions (configure or preview modes). */
	hideActions?: boolean;
	compact?: boolean;
	disabled?: boolean;
};

export function ApiQueryAddonSlider({
	planName,
	tiers,
	isAnnual,
	currentAddonQueries = 0,
	currentAddonPriceId,
	value,
	onValueChange,
	onSelectAddon,
	onRemoveAddon,
	isUpdating = false,
	hideActions = false,
	compact = false,
	disabled = false,
}: ApiQueryAddonSliderProps) {
	const sortedTiers = useMemo(
		() => [...tiers].sort((a, b) => a.queries - b.queries),
		[tiers],
	);

	const initialIndex = useMemo(
		() => sliderIndexForAddonQueries(currentAddonQueries, sortedTiers),
		[currentAddonQueries, sortedTiers],
	);

	const [internalIndex, setInternalIndex] = useState(initialIndex);
	const index = value ?? internalIndex;

	useEffect(() => {
		if (value === undefined) {
			setInternalIndex(initialIndex);
		}
	}, [initialIndex, value]);

	const setIndex = (next: number) => {
		if (value === undefined) {
			setInternalIndex(next);
		}
		onValueChange?.(next);
	};

	const selected = addonTierForSliderIndex(index, sortedTiers);
	const base = effectiveApiQueries(planName, 0);
	const addonQueries = addonQueriesForSliderIndex(index, sortedTiers);
	const total = effectiveApiQueries(planName, addonQueries);
	const priceCents = selected
		? isAnnual
			? selected.yearly_cents
			: selected.monthly_cents
		: 0;
	const selectedPriceId = selected
		? selectAddonPriceId(selected, isAnnual)
		: undefined;
	const hasAddonSelected = addonQueries > 0;
	const isCurrentSelection =
		hasAddonSelected &&
		currentAddonQueries === addonQueries &&
		(!currentAddonPriceId || currentAddonPriceId === selectedPriceId);
	const maxIndex = sortedTiers.length;
	const intervalSuffix = isAnnual ? "/yr" : "/mo";

	if (sortedTiers.length === 0) {
		return (
			<p className="text-muted-foreground text-sm">
				Query add-on packs are not configured yet.
			</p>
		);
	}

	return (
		<div className={cn("space-y-5", compact && "space-y-4")}>
			{!compact && (
				<div>
					<h3 className="font-semibold text-base tracking-tight">
						API query add-ons
					</h3>
					<p className="mt-1 text-muted-foreground text-sm">
						Your {planName} plan includes {formatQueryCount(base)} queries per
						month. Add capacity as you grow.
					</p>
				</div>
			)}

			<div className="space-y-3">
				<div className="flex items-center justify-between text-sm">
					<span className="text-muted-foreground">Extra queries</span>
					<span className="font-medium">
						{hasAddonSelected
							? `+${formatQueryCount(addonQueries)}/mo`
							: "None"}
					</span>
				</div>

				<input
					type="range"
					name="api-query-addon"
					min={0}
					max={maxIndex}
					step={1}
					value={index}
					aria-label="API query add-on amount"
					disabled={disabled}
					onChange={(e) => setIndex(Number(e.target.value))}
					className={cn(
						"h-2 w-full appearance-none rounded-full bg-muted accent-primary",
						disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
					)}
				/>

				<div className="flex justify-between gap-2 text-muted-foreground text-xs">
					<span>None</span>
					<span>
						+
						{formatQueryCount(
							sortedTiers[sortedTiers.length - 1]?.queries ?? 0,
						)}
					</span>
				</div>

				<div className="flex flex-wrap gap-2">
					<button
						type="button"
						disabled={disabled}
						onClick={() => setIndex(0)}
						className={cn(
							"rounded-full border px-3 py-1 text-xs font-medium transition-colors",
							index === 0
								? "border-primary bg-primary/10 text-primary"
								: "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground",
						)}
					>
						None
					</button>
					{sortedTiers.map((tier, tierIndex) => {
						const tierSliderIndex = tierIndex + 1;
						return (
							<button
								key={tier.queries}
								type="button"
								disabled={disabled}
								onClick={() => setIndex(tierSliderIndex)}
								className={cn(
									"rounded-full border px-3 py-1 text-xs font-medium transition-colors",
									index === tierSliderIndex
										? "border-primary bg-primary/10 text-primary"
										: "border-border/60 text-muted-foreground hover:border-primary/40 hover:text-foreground",
								)}
							>
								+{formatQueryCount(tier.queries)}
							</button>
						);
					})}
				</div>
			</div>

			<div className="rounded-xl border border-border/60 bg-muted/30 p-4 text-sm">
				<div className="flex justify-between gap-4">
					<span>Total monthly queries</span>
					<span className="font-semibold">{formatQueryCount(total)}</span>
				</div>
				{hasAddonSelected && (
					<div className="mt-2 flex justify-between gap-4 text-muted-foreground">
						<span>Add-on cost</span>
						<span>
							{formatMoney(priceCents)}
							{intervalSuffix}
						</span>
					</div>
				)}
			</div>

			{!hideActions && hasAddonSelected && selectedPriceId && onSelectAddon && (
				<Button
					type="button"
					className="w-full"
					disabled={isUpdating || isCurrentSelection}
					onClick={() => onSelectAddon(selectedPriceId, addonQueries)}
				>
					{isCurrentSelection ? "Current add-on" : "Update add-on"}
				</Button>
			)}
			{!hideActions && currentAddonQueries > 0 && onRemoveAddon && (
				<Button
					type="button"
					variant="outline"
					className="w-full"
					disabled={isUpdating}
					onClick={onRemoveAddon}
				>
					Remove add-on
				</Button>
			)}
		</div>
	);
}
